import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileStack,
  RefreshCw,
  SearchCheck,
  WifiOff,
} from "lucide-react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import TemplateFilters from "./component/TemplateFilters";
import TemplateTable from "./component/TemplateTable";
import TemplateEmpty from "./component/TemplateEmpty";
import TemplateHeader from "./component/TemplateHeader";
import TemplateExamples from "./component/TemplateExamples";
import { StagePage } from "src/stage/StageUi";
import { useNavigate } from "react-router-dom";
import TemplatePreviewModal from "./component/TemplatePreviewModal";
import templateService from "src/services/templateService";
import { showErrorToast } from "src/common/showErrorToast";

/* eslint-disable react-hooks/exhaustive-deps */

dayjs.extend(relativeTime);

function readSetup() {
  try {
    const parsed = JSON.parse(localStorage.getItem("whatsapp_setup") || "{}");
    return {
      workspaceId: parsed.workspaceId || parsed.workspace_id || null,
      accountId:
        parsed.whatsappAccountId ||
        parsed.whatsapp_account_id ||
        parsed.accountId ||
        parsed.account_id ||
        null,
    };
  } catch {
    return { workspaceId: null, accountId: null };
  }
}

const Template = () => {
  const { workspaceId, accountId } = useMemo(readSetup, []);

  const [templates, setTemplates] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [wabaCandidates, setWabaCandidates] = useState([]);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [language, setLanguage] = useState("ALL");
  const [viewMode, setViewMode] = useState("cards");
  // AUTO-WA-39: ready-made examples, opened from Home (?examples=1) or the header.
  const [showExamples, setShowExamples] = useState(() => new URLSearchParams(window.location.search).get("examples") === "1");
  const navigate = useNavigate();

  const params = useMemo(() => {
    const p = { per_page: 200 };
    if (search.trim()) p.search = search.trim();
    if (category !== "ALL") p.category = category;
    if (status !== "ALL") p.status = status;
    if (language !== "ALL") p.language = language;
    return p;
  }, [search, category, status, language]);

  const load = useCallback(async () => {
    if (!workspaceId || !accountId) {
      setLoading(false);
      setError("No WhatsApp account is connected for this workspace.");
      return;
    }
    try {
      setError("");
      const res = await templateService.list(workspaceId, accountId, params);
      setTemplates(res?.data?.data ?? []);
      setLastSync(res?.data?.meta?.last_sync ?? null);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to load templates.",
      );
    } finally {
      setLoading(false);
    }
  }, [workspaceId, accountId, params]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-sync once on open when we have never synced, the last run did not
  // complete, or the last successful run is stale.
  useEffect(() => {
    if (loading || syncing || !workspaceId || !accountId) return;
    const staleAfterMs = 30 * 60 * 1000;
    const finishedAt = lastSync?.finished_at
      ? new Date(lastSync.finished_at).getTime()
      : 0;
    const needsSync =
      !lastSync ||
      lastSync.status !== "completed" ||
      Date.now() - finishedAt > staleAfterMs;
    if (needsSync) {
      handleSync({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleSync = async ({ silent = false } = {}) => {
    if (!workspaceId || !accountId || syncing) return;
    try {
      setSyncing(true);
      const res = await templateService.sync(workspaceId, accountId);
      setLastSync(res?.data?.data ?? null);
      await load();
    } catch (err) {
      const data = err?.response?.data?.data;
      if (data) setLastSync(data);
      if (!silent) {
        showErrorToast(err, "Template sync failed.");
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleDetectWaba = async (wabaId) => {
    if (!workspaceId || !accountId || detecting) return;
    try {
      setDetecting(true);
      const res = await templateService.setWaba(workspaceId, accountId, wabaId);
      setWabaCandidates([]);
      toast.success(
        res?.data?.data?.name
          ? `Linked to "${res.data.data.name}"`
          : "WhatsApp Business Account linked.",
      );
      await handleSync();
    } catch (err) {
      if (err?.response?.status === 409) {
        setWabaCandidates(err.response.data?.data ?? []);
      } else {
        showErrorToast(err, "Could not detect the WhatsApp Business Account.");
      }
    } finally {
      setDetecting(false);
    }
  };

  const rows = useMemo(
    () =>
      templates.map((t) => ({
        ...t,
        quality: t.quality_score || "-",
        updatedAt: t.last_synced_at
          ? dayjs(t.last_synced_at).format("DD MMM YYYY")
          : "-",
      })),
    [templates],
  );

  const languages = useMemo(
    () => Array.from(new Set(templates.map((t) => t.language).filter(Boolean))),
    [templates],
  );

  const overview = useMemo(() => ({
    total: rows.length,
    approved: rows.filter((template) => template.status === "APPROVED").length,
    attention: rows.filter((template) => ["REJECTED", "PAUSED", "DISABLED"].includes(template.status)).length,
    languages: languages.length,
  }), [languages.length, rows]);

  return (
    <StagePage className="wa-legacy" wide label="Templates">

      <TemplateHeader
        onSync={() => handleSync()}
        syncing={syncing}
        overview={overview}
        onExamples={() => setShowExamples(true)}
      />

      {(showExamples || (!loading && !error && templates.length === 0)) && (
        <TemplateExamples
          onUse={(example) => navigate(`/templates/create?example=${example.id}`)}
          onClose={templates.length ? () => setShowExamples(false) : undefined}
        />
      )}

      <SyncBanner
        syncing={syncing}
        lastSync={lastSync}
        onRetry={() => handleSync()}
        onDetectWaba={handleDetectWaba}
        detecting={detecting}
        wabaCandidates={wabaCandidates}
      />

      <div className="flex w-full flex-1 flex-col">
        <div className="ws-card flex min-h-[460px] flex-1 flex-col">
          <TemplateFilters
            search={search}
            setSearch={setSearch}
            category={category}
            setCategory={setCategory}
            status={status}
            setStatus={setStatus}
            language={language}
            setLanguage={setLanguage}
            languages={languages}
            resultCount={rows.length}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />

          <div className="flex-1">
            {loading ? (
              <CenteredState icon={RefreshCw} spin text="Loading templates…" />
            ) : error ? (
              <CenteredState
                icon={AlertTriangle}
                text={error}
                tone="danger"
              />
            ) : rows.length ? (
              <TemplateTable
                templates={rows}
                onPreview={(t) => setPreviewTemplate(t)}
                viewMode={viewMode}
              />
            ) : (
              <TemplateEmpty />
            )}
          </div>
        </div>
      </div>

      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </StagePage>
  );
};

function SyncBanner({
  syncing,
  lastSync,
  onRetry,
  onDetectWaba,
  detecting,
  wabaCandidates = [],
}) {
  if (syncing) {
    return (
      <Banner tone="info" icon={RefreshCw} spin>
        Syncing templates from Meta…
      </Banner>
    );
  }
  if (!lastSync) return null;

  if (lastSync.status === "failed") {
    const msg = (lastSync.error_message || "").toLowerCase();
    const expired = msg.includes("token");
    const wabaProblem =
      msg.includes("waba") || msg.includes("business account");

    return (
      <Banner tone="danger" icon={expired ? WifiOff : AlertTriangle}>
        <span className="flex flex-wrap items-center gap-2">
          {expired
            ? "Meta authentication has expired. Reconnect the WhatsApp account, then sync again."
            : lastSync.error_message || "The last template sync failed."}

          {wabaProblem && !expired && wabaCandidates.length === 0 && (
            <button
              onClick={() => onDetectWaba()}
              disabled={detecting}
              className="inline-flex items-center gap-1 rounded-md border border-current/30 px-2 py-0.5 text-xs font-semibold disabled:opacity-60"
            >
              <SearchCheck size={12} className={detecting ? "animate-spin" : ""} />
              {detecting ? "Detecting…" : "Detect WhatsApp Business Account"}
            </button>
          )}

          {wabaCandidates.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <select
                defaultValue=""
                onChange={(e) => e.target.value && onDetectWaba(e.target.value)}
                className="rounded border border-current/30 bg-white/60 px-1 py-0.5 text-xs"
              >
                <option value="" disabled>
                  Choose an account…
                </option>
                {wabaCandidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name ? `${c.name} (${c.id})` : c.id}
                  </option>
                ))}
              </select>
            </span>
          )}

          <button
            onClick={onRetry}
            className="rounded-md border border-current/30 px-2 py-0.5 text-xs font-semibold"
          >
            Retry
          </button>
        </span>
      </Banner>
    );
  }

  if (lastSync.status === "partial") {
    return (
      <Banner tone="warning" icon={AlertTriangle}>
        Templates partially synced — some pages could not be fetched.
        <button
          onClick={onRetry}
          className="ml-3 rounded-md border border-current/30 px-2 py-0.5 text-xs font-semibold"
        >
          Retry
        </button>
      </Banner>
    );
  }

  return (
    <Banner tone="success" icon={CheckCircle2}>
      Synced with Meta · Last synced{" "}
      {lastSync.finished_at ? dayjs(lastSync.finished_at).fromNow() : "just now"}
      {typeof lastSync.templates_seen === "number"
        ? ` · ${lastSync.templates_seen} templates`
        : ""}
    </Banner>
  );
}

const TONES = { info: "info", success: "good", warning: "warn", danger: "bad" };

function Banner({ tone = "info", icon: Icon, spin = false, children }) {
  return (
    <div className={`wa-notice wa-notice--${TONES[tone]}`} role={tone === "danger" ? "alert" : "status"}>
      {Icon && <Icon size={15} className={spin ? "ws-spin" : ""} />}
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{children}</span>
    </div>
  );
}

function CenteredState({ icon: Icon, text, spin = false, tone }) {
  return (
    <div className="flex min-h-[390px] flex-col items-center justify-center px-6 text-center">
      <div className={`flex h-14 w-14 items-center justify-center rounded-[17px] border ${tone === "danger" ? "border-[#efd0ce] bg-[#fff4f3] text-[#b85d58] dark:border-[#633c3b] dark:bg-[#2b1f1e] dark:text-[#e6a09e]" : "border-[#cbe2eb] bg-[#edf8fd] text-[#147db5] dark:border-[#28516a] dark:bg-[#142a36] dark:text-[#6fc5eb]"}`}>
        {Icon ? (
          <Icon size={22} className={spin ? "animate-spin" : ""} />
        ) : (
          <FileStack size={22} />
        )}
      </div>
      <p
        className={`mt-4 max-w-md text-[11px] font-semibold leading-5 ${
          tone === "danger" ? "text-[#98504f] dark:text-[#e6a09e]" : "text-[#708593] dark:text-[#809aa8]"
        }`}
      >
        {text}
      </p>
    </div>
  );
}

export default Template;
